import { getCartItemByProductId } from '@/js/api/cart.ts';
import { useCartTotal } from '@/js/vue/composable/cart-total.ts';

const SELECTORS = {
  FORM: '.js-product-cart-form',
  COUNTER_BUTTON: '.js-counter-button',
  COUNTER_INPUT: '.js-counter-input',
  PRODUCT_ID: 'input[name="product_id"]',
  MAX_QUANTITY: 'input[name="max_quantity"]',
  SUBMIT: 'button[type="submit"]',
  ERROR: '.js-counter-error',
} as const;

const MESSAGES = {
  MAX_IN_CART: 'У вас максимальное количество в корзине',
  LIMIT_EXCEEDED: 'В настоящее время можно приобрести только данное количество товаров',
  API_ERROR: 'Произошла ошибка при проверке доступности товара',
} as const;

type ValidationResult = {
  isValid: true
} | {
  isValid: false
  error: string
};

type AddProductItem = (productId: number, quantity: number) => Promise<unknown> | void;

export class ProductCartForm {
  private form!: HTMLFormElement;
  private counterInput!: HTMLInputElement;
  private productIdInput!: HTMLInputElement;
  private maxQuantity!: HTMLInputElement;
  private submitButton!: HTMLButtonElement;
  private errorElement!: HTMLElement | null;

  readonly addProductItem: AddProductItem;
  private clickHandler: EventListener | null = null;
  private submitHandler: EventListener | null = null;
  private isInitialized = false;

  constructor() {
    const { addProductItem } = useCartTotal();
    this.addProductItem = addProductItem;
  }

  async init(): Promise<() => void> {
    const formElement = document.querySelector<HTMLFormElement>(SELECTORS.FORM);

    if (!formElement) {
      return () => {};
    }

    this.initializeElements(formElement);
    if (!this.isInitialized) {
      return () => {};
    }

    await this.updateSubmitButtonState();

    this.attachEventListeners();

    return () => this.detachEventListeners();
  }

  private initializeElements(form: HTMLFormElement): void {
    this.form = form;
    this.errorElement = document.querySelector<HTMLElement>(SELECTORS.ERROR);

    const counterInput = form.querySelector<HTMLInputElement>(SELECTORS.COUNTER_INPUT);
    const productIdInput = form.querySelector<HTMLInputElement>(SELECTORS.PRODUCT_ID);
    const maxQuantity = form.querySelector<HTMLInputElement>(SELECTORS.MAX_QUANTITY);
    const submitButton = form.querySelector<HTMLButtonElement>(SELECTORS.SUBMIT);

    if (!counterInput || !productIdInput || !maxQuantity || !submitButton) {
      console.error('Не все необходимые элементы формы найдены');
      this.isInitialized = false;
      return;
    }

    this.counterInput = counterInput;
    this.productIdInput = productIdInput;
    this.maxQuantity = maxQuantity;
    this.submitButton = submitButton;

    this.isInitialized = true;
  }

  private async updateSubmitButtonState(): Promise<void> {
    const productQuantityInfo = await this.getProductQuantityInfo();

    if (productQuantityInfo && productQuantityInfo.quantity >= productQuantityInfo.maxQuantity) {
      this.submitButton.disabled = true;
      this.showErrorMessage(MESSAGES.MAX_IN_CART);
      return;
    }

    this.submitButton.disabled = false;
    this.hideErrorMessage();
  }

  private async getProductQuantityInfo() {
    if (!this.productIdInput.value)
      return null;

    const productId = Number.parseInt(this.productIdInput.value, 10);
    if (Number.isNaN(productId))
      return null;

    return getCartItemByProductId(productId);
  }

  private async validateQuantity(newValue: number): Promise<ValidationResult> {
    try {
      const productQuantityInfo = await this.getProductQuantityInfo();

      if (productQuantityInfo) {
        const isValid = newValue + productQuantityInfo.quantity <= productQuantityInfo.maxQuantity;
        return isValid
          ? { isValid: true }
          : { isValid: false, error: MESSAGES.LIMIT_EXCEEDED };
      }

      const isValid = newValue <= Number.parseInt(this.maxQuantity.value, 10);
      return isValid
        ? { isValid: true }
        : { isValid: false, error: MESSAGES.LIMIT_EXCEEDED };
    }
    catch {
      return { isValid: false, error: MESSAGES.API_ERROR };
    }
  }

  private async updateProductQuantity(newValue: number): Promise<void> {
    const result = await this.validateQuantity(newValue);

    if (!result.isValid) {
      this.showErrorMessage(result.error);
      return;
    }

    this.counterInput.value = newValue.toString();
    this.hideErrorMessage();
    await this.updateSubmitButtonState();
  }

  private showErrorMessage(message: string): void {
    if (!this.errorElement)
      return;

    this.errorElement.textContent = message;
    this.errorElement.classList.remove('hide');
  }

  private hideErrorMessage(): void {
    if (!this.errorElement)
      return;

    this.errorElement.classList.add('hide');
  }

  private createClickHandler(): EventListener {
    return (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>(SELECTORS.COUNTER_BUTTON);

      if (!button)
        return;

      event.preventDefault();

      const action = button.dataset.action as 'increment' | 'decrement';
      const currentValue = Number.parseInt(this.counterInput.value, 10) || 0;
      const newValue = action === 'increment' ? currentValue + 1 : Math.max(1, currentValue - 1);

      void this.updateProductQuantity(newValue);
    };
  }

  private createSubmitHandler(): EventListener {
    return (event: Event) => {
      event.preventDefault();

      const productId = Number.parseInt(this.productIdInput.value, 10);
      const quantity = Number.parseInt(this.counterInput.value, 10);

      void (async () => {
        await this.addProductItem(productId, quantity);
        await this.updateSubmitButtonState();
        this.counterInput.value = '1';
      })();
    };
  }

  private attachEventListeners(): void {
    this.clickHandler = this.createClickHandler();
    this.submitHandler = this.createSubmitHandler();

    this.form.addEventListener('click', this.clickHandler);
    this.form.addEventListener('submit', this.submitHandler);
  }

  private detachEventListeners(): void {
    if (!this.clickHandler || !this.submitHandler)
      return;

    this.form.removeEventListener('click', this.clickHandler);
    this.form.removeEventListener('submit', this.submitHandler);
  }
}

export async function initProductCartForms(): Promise<() => void> {
  const cartForm = new ProductCartForm();
  return cartForm.init();
}
